import * as fs from "node:fs";
import * as path from "node:path";

import {
  CliError,
  EXIT_NEEDS_AGENT,
  EXIT_OK,
  EXIT_USAGE,
  sha256Text,
  type CommandOutput,
} from "../../common.js";
import {
  migrateScriptToVersion,
  parseScriptMigrationStepArtifacts,
} from "../../domain/script/migrations/registry.js";
import type { ScriptMigrationStepArtifact } from "../../domain/script/migrations/types.js";
import {
  isSupportedScriptVersion,
  scriptVersionOf,
  SUPPORTED_SCRIPT_VERSIONS,
  validateScriptDocument,
  type ScriptVersion,
} from "../../domain/script/dispatch.js";
import { atomicWriteJson, withScriptFileLock } from "../../infra/script-working-copy.js";
import { isDict, type Dict } from "../../domain/script/shared.js";

interface MigrationManifest {
  readonly version: 1;
  readonly source_version: ScriptVersion;
  readonly target_version: ScriptVersion;
  readonly source_path: string;
  readonly source_sha256: string;
  readonly steps: readonly ScriptMigrationStepArtifact[];
}

function stringOption(opts: Dict, name: string): string {
  const value = opts[name];
  return typeof value === "string" ? value.trim() : "";
}

function numberOption(opts: Dict, name: string): number | null {
  const value = stringOption(opts, name);
  if (!/^\d+$/.test(value)) return null;
  return Number(value);
}

function requireTargetVersion(opts: Dict): ScriptVersion {
  const value = numberOption(opts, "to");
  if (isSupportedScriptVersion(value)) return value;
  throw new CliError("A supported migration target version is required.", {
    exitCode: EXIT_USAGE,
    errorCode: "MIGRATE_TARGET_INVALID",
    hint: "Pass --to <version> with a supported Script version.",
    data: {
      expected: SUPPORTED_SCRIPT_VERSIONS.map(String),
      actual: [stringOption(opts, "to") || "<empty>"],
    },
  });
}

function defaultManifestPath(outputPath: string): string {
  const extension = path.extname(outputPath);
  const stem = extension.length > 0
    ? outputPath.slice(0, -extension.length)
    : outputPath;
  return `${stem}.migration.json`;
}

function requireDistinctPaths(sourcePath: string, outputPath: string, manifestPath: string): void {
  const resolved = [sourcePath, outputPath, manifestPath].map((item) => path.resolve(item));
  if (resolved[0] === resolved[1]) {
    throw new CliError("The migration destination must differ from the source.", {
      exitCode: EXIT_USAGE,
      errorCode: "MIGRATE_SAME_PATH",
      hint: "Choose a separate --output path. The source file stays unchanged.",
      data: { actual: [sourcePath] },
    });
  }
  if (resolved[0] === resolved[2] || resolved[1] === resolved[2]) {
    throw new CliError("The migration manifest needs its own file path.", {
      exitCode: EXIT_USAGE,
      errorCode: "MIGRATE_MANIFEST_PATH",
      hint: "Choose a --manifest path separate from the source and output files.",
      data: { actual: [manifestPath] },
    });
  }
}

function assertWritableDestination(filePath: string, force: boolean): void {
  if (!force && fs.existsSync(filePath)) {
    throw new CliError("Migration destination already exists.", {
      exitCode: EXIT_USAGE,
      errorCode: "MIGRATE_OUTPUT_EXISTS",
      hint: "Choose a new path, or pass --force to replace the migration artifact.",
      data: { actual: [filePath] },
    });
  }
}

function readSource(sourcePath: string): { raw: string; value: unknown } {
  let raw: string;
  try {
    raw = fs.readFileSync(sourcePath, "utf8");
  } catch (error) {
    throw new CliError("Cannot read the source script.", {
      exitCode: EXIT_USAGE,
      errorCode: "MIGRATE_SOURCE_READ_FAILED",
      hint: "Pass an existing JSON file with --script-path.",
      data: { actual: [sourcePath, error instanceof Error ? error.message : String(error)] },
    });
  }
  try {
    return { raw, value: JSON.parse(raw) };
  } catch (error) {
    throw new CliError("The source script is not valid JSON.", {
      exitCode: EXIT_USAGE,
      errorCode: "MIGRATE_SOURCE_JSON_INVALID",
      hint: "Repair the JSON syntax, then rerun migrate.",
      data: { actual: [error instanceof Error ? error.message : String(error)] },
    });
  }
}

function readPriorManifest(manifestPath: string, force: boolean): MigrationManifest | undefined {
  if (!force || !fs.existsSync(manifestPath)) return undefined;
  let value: unknown;
  try {
    value = JSON.parse(fs.readFileSync(manifestPath, "utf8"));
  } catch (error) {
    throw new CliError("Cannot reuse the existing migration manifest.", {
      exitCode: EXIT_USAGE,
      errorCode: "MIGRATE_MANIFEST_INVALID",
      hint: "Restore the migration manifest, or choose a new --manifest path.",
      data: { actual: [manifestPath, error instanceof Error ? error.message : String(error)] },
    });
  }
  if (!isDict(value)
    || value["version"] !== 1
    || !isSupportedScriptVersion(value["source_version"])
    || !isSupportedScriptVersion(value["target_version"])
    || typeof value["source_path"] !== "string"
    || typeof value["source_sha256"] !== "string") {
    throw new CliError("Cannot reuse the existing migration manifest.", {
      exitCode: EXIT_USAGE,
      errorCode: "MIGRATE_MANIFEST_INVALID",
      hint: "Restore the migration manifest, or choose a new --manifest path.",
      data: { actual: [manifestPath] },
    });
  }
  return {
    version: 1,
    source_version: value["source_version"],
    target_version: value["target_version"],
    source_path: value["source_path"],
    source_sha256: value["source_sha256"],
    steps: parseScriptMigrationStepArtifacts(value["steps"]),
  };
}

export async function commandMigrate(opts: Dict): Promise<[CommandOutput, number]> {
  const sourceRawPath = stringOption(opts, "script_path");
  const outputRawPath = stringOption(opts, "output");
  if (!sourceRawPath || !outputRawPath) {
    throw new CliError("Migration paths are required.", {
      exitCode: EXIT_USAGE,
      errorCode: "MIGRATE_PATH_MISSING",
      hint: "Pass --script-path <source.json> and --output <target.json>.",
      data: { expected: ["--script-path <source.json>", "--output <target.json>"] },
    });
  }

  const sourcePath = path.resolve(sourceRawPath);
  const outputPath = path.resolve(outputRawPath);
  const manifestPath = path.resolve(stringOption(opts, "manifest") || defaultManifestPath(outputPath));
  const targetVersion = requireTargetVersion(opts);
  const force = Boolean(opts["force"]);
  requireDistinctPaths(sourcePath, outputPath, manifestPath);

  return withScriptFileLock(outputPath, async () => {
    assertWritableDestination(outputPath, force);
    assertWritableDestination(manifestPath, force);

    const sourceDocument = readSource(sourcePath);
    const sourceVersion = scriptVersionOf(sourceDocument.value);
    if (targetVersion <= sourceVersion) {
      throw new CliError("The migration target must be newer than the source script.", {
        exitCode: EXIT_USAGE,
        errorCode: "MIGRATE_TARGET_NOT_NEWER",
        data: { expected: [`>${sourceVersion}`], actual: [String(targetVersion)] },
      });
    }
    const sourceValidation = validateScriptDocument(sourceDocument.value);
    const priorManifest = readPriorManifest(manifestPath, force);
    const migrated = migrateScriptToVersion(
      sourceDocument.value,
      targetVersion,
      priorManifest?.steps,
    );
    const validation = validateScriptDocument(migrated.script);
    const manifest: MigrationManifest = {
      version: 1,
      source_version: migrated.sourceVersion,
      target_version: migrated.targetVersion,
      source_path: sourcePath,
      source_sha256: sha256Text(sourceDocument.raw),
      steps: migrated.steps,
    };

    atomicWriteJson(manifestPath, {
      ...manifest,
      steps: manifest.steps.map((step) => ({
        source_version: step.sourceVersion,
        target_version: step.targetVersion,
        identity_map: step.identityMap,
      })),
    });
    atomicWriteJson(outputPath, migrated.script);

    const data = {
      op: "script.migrate",
      changed: true,
      source_script_path: sourcePath,
      script_path: outputPath,
      manifest_path: manifestPath,
      source_version: migrated.sourceVersion,
      target_version: migrated.targetVersion,
      source_validation: {
        ok: sourceValidation.ok,
        errors: sourceValidation.errors.length,
        warnings: sourceValidation.warnings.length,
      },
      validation: {
        ok: validation.ok,
        errors: validation.errors.length,
        warnings: validation.warnings.length,
        issues: validation.errors.slice(0, 100),
      },
    };
    const text = validation.ok
      ? [`Script v${migrated.targetVersion}: ${outputPath}`, `Migration manifest: ${manifestPath}`, "validation: ok"].join("\n")
      : [
          `Script v${migrated.targetVersion}: ${outputPath}`,
          `Migration manifest: ${manifestPath}`,
          `validation: ${validation.errors.length} errors; repair before production use`,
        ].join("\n");
    return [{ text, data }, validation.ok ? EXIT_OK : EXIT_NEEDS_AGENT];
  });
}
